import { Remove } from '@mui/icons-material';
import {
  Button,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  Stack,
  Typography,
} from '@mui/material';
import { FieldArray, Form, Formik } from 'formik';
import React, { useState } from 'react';
import * as Yup from 'yup';
import CustomButton from '../../elements/CustomButton';
import InputField from '../../elements/InputField';
import { ProductSpecsOptions } from '../../../staticData/tableHead';
import FormattedTextArea from '../../elements/FormattedTextArea';

const EditSpecsForm = ({ onSubmit, detail }) => {
  const [draggedIndex, setDraggedIndex] = useState(null);

  return (
    <Formik
      initialValues={{
        type: detail?.type,
        name: detail?.name || '',
        description: detail?.description || [''],
      }}
      validationSchema={Yup.object().shape({
        name: Yup.string().when(['type', 'description'], (type) => {
          return type === 'spec' || Yup.string().notRequired();
        }),
        description: Yup.array()
          .of(Yup.string())
          .required('Description is required'),
      })}
      onSubmit={onSubmit}>
      {({
        errors,
        handleBlur,
        handleSubmit,
        touched,
        values,
        handleChange,
      }) => (
        <Form noValidate onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Stack spacing={1}>
                <InputField
                  label="Type"
                  id="type"
                  type="text"
                  value={
                    ProductSpecsOptions.find(
                      (option) => option.value === values.type
                    )?.label || values.type
                  }
                  name="type"
                  disabled
                  fullWidth
                />
              </Stack>
            </Grid>
            {values.type === 'spec' && (
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <InputField
                    label="Name"
                    id="name"
                    type="text"
                    value={values.name}
                    name="name"
                    fullWidth
                    placeholder="Name"
                    error={Boolean(touched.name && errors.name)}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                </Stack>
              </Grid>
            )}
            <Grid item xs={12}>
              <Stack spacing={1}>
                <InputLabel variant="h6">Description</InputLabel>
                {values.type !== 'spec' && (
                  <Typography
                    variant="caption"
                    sx={{ mb: 2, color: 'text.secondary' }}>
                    Drag and drop to reorder bullet points
                  </Typography>
                )}
                <FieldArray
                  name="description"
                  render={(arrayHelpers) => (
                    <div>
                      {values.description.map((point, index) => (
                        <div
                          key={index}
                          draggable
                          onDragStart={() => setDraggedIndex(index)}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={() => {
                            const pointsCopy = [...values.description];
                            const draggedPoint = pointsCopy[draggedIndex];
                            pointsCopy.splice(draggedIndex, 1);
                            pointsCopy.splice(index, 0, draggedPoint);
                            arrayHelpers.form.setFieldValue(
                              'description',
                              pointsCopy
                            );
                          }}
                          style={{
                            cursor: 'move',
                          }}>
                          <FormattedTextArea
                            id={`description[${index}]`}
                            name={`description[${index}]`}
                            value={point}
                            placeholder="Enter description"
                            onChange={handleChange}
                          />
                          {values.type !== 'spec' && (
                            <IconButton
                              onClick={() => arrayHelpers.remove(index)}
                              color="secondary"
                              sx={{ paddingBottom: 0 }}
                              title='Remove point'
                              disabled={index === 0}>
                              <Remove />
                            </IconButton>
                          )}
                        </div>
                      ))}
                      {values.type !== 'spec' && (
                        <Stack
                          direction="row"
                          justifyContent="center"
                          spacing={2}>
                          <Button
                            onClick={() => arrayHelpers.push('')}
                            color="primary"
                            variant="text"
                            sx={{ mt: '10px' }}>
                            Add row
                          </Button>
                        </Stack>
                      )}
                    </div>
                  )}
                />
              </Stack>
            </Grid>
            {errors.submit && (
              <Grid item xs={12}>
                <FormHelperText error>{errors.submit}</FormHelperText>
              </Grid>
            )}
            <Grid item xs={12}>
              <CustomButton />
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default EditSpecsForm;
